import { GetPickupGuideOutput, TRIP_TYPE } from '@allocab/allocab-commons-shared-types';
import {
  CalendarDaysSolid,
  CircleUserSolid,
  LocationDotSolid,
  PenSolid,
  PlaneSolid,
  SignsPostSolid,
  TicketSolid,
  TrainSubwaySolid,
} from '@allocab/allocab-icons';
import { TripOutput } from '@allocab/allocab-passenger-shared-types';
import clsx from 'clsx';
import { ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { TransportPicker } from './transport-picker/TransportPicker';
import { TripGuideDownloadButton } from './TripGuideDownloadButton';
import arrivalPinImage from '../../assets/images/arrival-pin.png';
import departurePinImage from '../../assets/images/departure-pin.png';
import { t } from '../../services/translation/translation';
import { formatDateForParisTZ } from '../../utils/date';
import { getStepForTrip, isTransportTrip } from '../../utils/trip';

interface Props {
  trip: TripOutput;
  dataGuide?: GetPickupGuideOutput;
}

const renderTransportIcon = (transportType?: TRIP_TYPE): ReactNode =>
  transportType === 'FARETYPE_STATION' ? (
    <TrainSubwaySolid width={16} height={16} className="fill-primary mr-2" />
  ) : transportType === 'FARETYPE_AIRPORT' ? (
    <PlaneSolid width={16} height={16} className="fill-primary mr-2" />
  ) : (
    <TicketSolid width={16} height={16} className="fill-primary mr-2" />
  );

export const TripResume = ({ trip, dataGuide }: Props) => {
  const [transportType] = useState(trip.tripVars.tripType);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const isTransport = isTransportTrip(trip);
  const is1PlusHourBefore = Date.now() + 60 * 60 * 1000 < trip.resume.departureAt;
  const step = getStepForTrip(trip);
  const isBeforePickup =
    step === 'BOOKING_CONFIRMED' ||
    step === 'SEARCHING_DRIVER' ||
    step === 'DRIVER_APPROACHING' ||
    step === 'DRIVER_IN_PLACE';
  const canEditTransportNumber = isBeforePickup && is1PlusHourBefore;

  return (
    <>
      {isBeforePickup &&
      ((trip.tripVars.reception && trip.resume.pickUpPoint?.signDescription) ||
        trip.resume.pickUpPoint?.description) ? (
        <div className="flex flex-col shadow-md shadow-shadow ">
          <div className="flex w-full p-2 gap-x-2 min-h-200 items-center">
            <div className="w-12 h-12 bg-cta rounded-full flex items-center justify-center">
              <LocationDotSolid width={20} height={20} className="fill-text_light" />
            </div>
            <div className="flex-1">
              <p className="text-lg font-bold">{t('trip-route.pickup-point.title')}</p>
              <p>
                {trip.tripVars.reception && trip.resume.pickUpPoint?.signDescription
                  ? trip.resume.pickUpPoint.signDescription
                  : trip.resume.pickUpPoint?.description}
              </p>
            </div>
          </div>
          {dataGuide?.urlSuffix ? <TripGuideDownloadButton dataGuide={dataGuide} /> : null}
        </div>
      ) : null}

      <div className="flex-col flex w-full shadow-md shadow-shadow p-2 gap-y-2 min-h-200 items-center">
        <div className="flex items-center flex-col w-full">
          <p className="flex items-center w-full">
            <SignsPostSolid width={16} height={16} className="fill-primary mr-2" />
            <span>{`R${trip.id}`}</span>
          </p>
        </div>
        <div className="flex items-center flex-col w-full">
          <p className="flex items-center w-full">
            <img className="flex mr-2 w-4 h-auto" alt="depart" src={departurePinImage} />
            <span>{trip?.resume?.start?.formattedAddress}</span>
          </p>
        </div>
        {isTransport ? (
          <>
            <div
              className={clsx('flex items-center flex-col w-full', canEditTransportNumber && 'cursor-pointer')}
              onClick={canEditTransportNumber ? () => setIsModalVisible(true) : undefined}
            >
              <p className="flex items-center w-full">
                {renderTransportIcon(trip.tripVars.tripType)}
                {trip.tripVars.transportNumber ? (
                  <span className="uppercase">{trip.tripVars.transportNumber}</span>
                ) : (
                  <span className={twMerge('text-cta', canEditTransportNumber && 'underline')}>
                    {canEditTransportNumber ? t('transport-number-modal.empty') : '--'}
                  </span>
                )}
                {canEditTransportNumber ? (
                  <div className="px-2">
                    <PenSolid width={16} height={16} className="fill-cta" />
                  </div>
                ) : null}
              </p>
            </div>
            {isModalVisible ? (
              <TransportPicker
                trip={trip}
                type={
                  transportType === 'FARETYPE_AIRPORT'
                    ? 'flight'
                    : null || transportType === 'FARETYPE_STATION'
                    ? 'train'
                    : null
                }
                onClose={() => setIsModalVisible(false)}
              />
            ) : null}
          </>
        ) : null}
        <div className="flex items-center flex-col w-full">
          <p className="flex items-center w-full">
            <img className="flex mr-2 w-4 h-auto " alt="arrivee" src={arrivalPinImage} />
            <span>{trip?.resume?.end?.formattedAddress}</span>
          </p>
        </div>
      </div>
      <div className="flex-col flex w-full shadow-md shadow-shadow p-2 gap-y-2 min-h-200 items-center">
        <div className="flex items-center flex-col w-full">
          <p className="flex items-center w-full ">
            <CalendarDaysSolid width={16} height={16} className="mr-2" />
            {t('share-route.trip-detail.departure')}
            <strong className="ml-1 fontSize1-2rem">
              {trip?.resume?.departureAt ? formatDateForParisTZ(trip.resume.departureAt, 'DD/MM/YYYY - HH:mm') : '-'}
            </strong>
          </p>
        </div>

        <div className="flex items-center flex-col w-full">
          <p className="flex items-center w-full ">
            <CircleUserSolid width={16} height={16} className="mr-2" />
            <span className="capitalize">
              {trip.passenger.firstName} {trip.passenger.lastName}
            </span>
          </p>
        </div>
      </div>
    </>
  );
};
